import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { AdminLayout } from "@/pages/admin/adminShared";
import { formatDate, formatTime, formatDateShort } from "@/lib/format";
import { Users, Music, Calendar, CalendarDays, ListChecks, AlertTriangle, Clock, Plus, Activity } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center justify-between"><Icon size={20} className="text-[#B04B26]" /><span className="font-display text-3xl font-extrabold text-[#2D1E18]">{value}</span></div>
      <p className="mt-2 text-sm font-semibold text-[#6E5B51]">{label}</p>
    </div>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-[#2D1E18]"><Icon size={18} className="text-[#B04B26]" /> {title}</h2>
      {children}
    </section>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["admin-dashboard"], queryFn: async () => (await api.get("/admin/dashboard")).data });

  return (
    <AdminLayout role={user?.role} title="Admin dashboard" subtitle={`What's happening today — ${data?.today_name || ""}`}>
      {isLoading ? <Spinner /> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label="Members" value={data.counts.members} icon={Users} />
            <Stat label="Active dances" value={data.counts.dances} icon={Music} />
            <Stat label="Classes" value={data.counts.classes} icon={Calendar} />
            <Stat label="Upcoming events" value={data.counts.events} icon={CalendarDays} />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel title="Today's classes" icon={Clock}>
              {data.today_classes?.length ? data.today_classes.map((c) => (
                <div key={c.id} className="flex items-center justify-between border-b border-border py-2 last:border-0">
                  <span className="font-semibold text-[#2D1E18]">{c.name}</span>
                  <span className="text-sm text-[#6E5B51]">{formatTime(c.start_time)} · {c.venue?.name}</span>
                </div>
              )) : <p className="text-sm text-[#6E5B51]">No classes scheduled today.</p>}
            </Panel>

            <Panel title="Sessions awaiting completion" icon={ListChecks}>
              {data.pending_sessions?.length ? data.pending_sessions.map((s) => (
                <Link key={s.id} to="/admin/sessions" className="flex items-center justify-between border-b border-border py-2 last:border-0 hover:text-[#B04B26]">
                  <span className="font-semibold text-[#2D1E18]">{s.class_name}</span>
                  <span className="text-sm text-[#6E5B51]">{formatDateShort(s.date)}</span>
                </Link>
              )) : <p className="text-sm text-[#6E5B51]">Nothing to complete. Nice work!</p>}
            </Panel>

            <Panel title="Playlists needing prep" icon={Plus}>
              {data.needs_playlist?.length ? data.needs_playlist.map((s) => (
                <Link key={s.id} to="/admin/sessions" className="flex items-center justify-between border-b border-border py-2 last:border-0 hover:text-[#B04B26]">
                  <span className="font-semibold text-[#2D1E18]">{s.class_name}</span>
                  <span className="text-sm text-[#6E5B51]">{formatDateShort(s.date)}</span>
                </Link>
              )) : <p className="text-sm text-[#6E5B51]">All upcoming sessions have playlists.</p>}
            </Panel>

            <Panel title="Current announcements" icon={AlertTriangle}>
              {data.announcements?.length ? data.announcements.map((a) => (
                <div key={a.id} className="border-b border-border py-2 last:border-0">
                  <p className="font-semibold text-[#2D1E18]">{a.title}</p>
                  <p className="text-sm text-[#6E5B51]">{a.type} · {a.priority}</p>
                </div>
              )) : <p className="text-sm text-[#6E5B51]">No active announcements.</p>}
            </Panel>
          </div>

          <Panel title="Instructor activity" icon={Activity}>
            {data.recent_activity?.length ? data.recent_activity.map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-3 border-b border-border py-2 last:border-0" data-testid={`activity-${a.id}`}>
                <p className="text-sm text-[#2D1E18]"><span className="font-semibold">{a.actor_name}</span> {a.summary}</p>
                <span className="shrink-0 text-xs text-[#6E5B51]">{timeAgo(a.created_at)}</span>
              </div>
            )) : <p className="text-sm text-[#6E5B51]">No recent activity yet. Once instructors post dances or publish playlists, it shows here.</p>}
          </Panel>

          <Panel title="Recently added dances" icon={Music}>
            <div className="flex flex-wrap gap-2">
              {data.recent_dances?.map((d) => <Link key={d.id} to={`/dances/${d.slug}`} className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold text-[#5C4A42] hover:text-[#B04B26]">{d.name}</Link>)}
            </div>
          </Panel>
        </div>
      )}
    </AdminLayout>
  );
}
